import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  LuArrowLeft,
  LuArrowRight,
  LuCalendar,
  LuClock,
} from 'react-icons/lu';
import './PublicSite.css';
import './BlogPages.css';
import Seo, {
  SITE_URL,
  buildArticleSchema,
  buildBlogSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
} from '../components/Seo';
import {
  BLOG_PATH,
  getAllPosts,
  getPostBySlug,
  getPostPath,
  getRelatedPosts,
} from '../data/blogPosts';

const startAccount = () => {
  window.dispatchEvent(new Event('open-auth-modal'));
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

function formatDate(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function coverUrl(post) {
  return post.coverImage ? `${SITE_URL}${post.coverImage}` : undefined;
}

function BlogBackLink() {
  return (
    <nav className="asset-back-nav" aria-label="Blog">
      <Link to={BLOG_PATH} className="asset-back-link">
        <LuArrowLeft aria-hidden="true" /> All articles
      </Link>
    </nav>
  );
}

function BlogCTA() {
  return (
    <>
      <section className="sub-cta">
        <div>
          <p className="eyebrow">GET STARTED</p>
          <h2>Record your assets and nominees in one secure place</h2>
          <p>Free to begin. The legal nomination stays with the bank—the map of what you own should not live only in your head.</p>
        </div>
        <button type="button" className="button button-accent" onClick={startAccount}>
          Secure Assets <LuArrowRight aria-hidden="true" />
        </button>
      </section>
      <div className="mobile-sticky-cta">
        <button type="button" className="button button-accent" onClick={startAccount}>
          Secure Assets
        </button>
      </div>
    </>
  );
}

function PostMeta({ post }) {
  return (
    <div className="blog-meta">
      <span className="blog-meta-category">{post.category}</span>
      <span>
        <LuCalendar aria-hidden="true" /> {formatDate(post.date)}
      </span>
      <span>
        <LuClock aria-hidden="true" /> {post.readTime}
      </span>
    </div>
  );
}

function PostCard({ post, featured = false }) {
  return (
    <Link
      to={getPostPath(post.slug)}
      className={`blog-card ${featured ? 'blog-card-featured' : ''}`}
    >
      {post.coverImage ? (
        <div className="blog-card-media">
          <img src={post.coverImage} alt="" />
        </div>
      ) : null}
      <div className="blog-card-body">
        <PostMeta post={post} />
        <h2>{post.title}</h2>
        <p>{post.excerpt}</p>
        <span className="blog-card-link">
          Read article <LuArrowRight aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}

const markdownParts = (markdown) => {
  const parts = [];
  const pattern = /:::(\w+)[ \t]*([^\n]*)\n([\s\S]*?):::/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(markdown))) {
    if (match.index > lastIndex) {
      parts.push({ type: 'md', text: markdown.slice(lastIndex, match.index) });
    }
    parts.push({
      type: match[1],
      title: match[2].trim(),
      text: match[3].trim(),
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < markdown.length) {
    parts.push({ type: 'md', text: markdown.slice(lastIndex) });
  }

  return parts;
};

const markdownComponents = {
  img: ({ src, alt }) => (
    <figure className="blog-figure">
      <img src={src} alt={alt || ''} loading="lazy" />
      {alt ? <figcaption>{alt}</figcaption> : null}
    </figure>
  ),
  p: ({ node, children }) => {
    const hasImage = node?.children?.some((child) => child.tagName === 'img');
    if (hasImage) return <>{children}</>;
    return <p>{children}</p>;
  },
};

function BlogMarkdown({ markdown }) {
  return markdownParts(markdown).map((part, index) => {
    if (part.type === 'stat') {
      return (
        <div className="blog-stat" key={index}>
          <strong>{part.title}</strong>
          <span>{part.text}</span>
        </div>
      );
    }
    if (part.type === 'callout') {
      return (
        <aside className="blog-callout" key={index}>
          <strong>{part.title}</strong>
          <p>{part.text}</p>
        </aside>
      );
    }
    return (
      <ReactMarkdown key={index} remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {part.text}
      </ReactMarkdown>
    );
  });
}

function BlogIndexPage() {
  const posts = getAllPosts();
  const featured = posts.find((post) => post.featured) || posts[0];
  const rest = posts.filter((post) => post.slug !== featured.slug);

  return (
    <>
      <Seo
        title="Blog"
        description="Guides on nominees, unclaimed assets, and how Indian families can record what they own so loved ones are not left guessing."
        path={BLOG_PATH}
        keywords="keepmyasset blog, what is a nominee, nominee guide India, unclaimed money, PPF EPF insurance nominee"
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: BLOG_PATH },
          ]),
          buildBlogSchema(posts),
        ]}
      />
      <div className="public-site public-page blog-page">
        <h1 className="blog-page-title">KeepMyAsset Blog</h1>
        <section className="sub-section blog-index-section">
          <PostCard post={featured} featured />
          {rest.length > 0 ? (
            <div className="blog-card-grid">
              {rest.map((post) => (
                <PostCard post={post} key={post.slug} />
              ))}
            </div>
          ) : null}
        </section>

        <BlogCTA />
      </div>
    </>
  );
}

function BlogArticlePage({ post }) {
  const related = getRelatedPosts(post);
  const path = getPostPath(post.slug);
  const image = coverUrl(post);

  return (
    <>
      <Seo
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        path={path}
        type="article"
        image={image}
        keywords={post.keywords}
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: BLOG_PATH },
            { name: post.title, path },
          ]),
          buildArticleSchema({
            title: post.title,
            description: post.seoDescription || post.excerpt,
            path,
            datePublished: post.date,
            dateModified: post.updated || post.date,
            image,
          }),
          ...(post.faqs?.length ? [buildFaqSchema(post.faqs)] : []),
        ]}
      />
      <div className="public-site public-page blog-page">
        <BlogBackLink />

        <article className="blog-article">
          <header className="blog-article-header">
            <PostMeta post={post} />
            <h1>{post.title}</h1>
            <p className="lead">{post.excerpt}</p>
          </header>

          {post.coverImage ? (
            <figure className="blog-cover">
              <img src={post.coverImage} alt={post.coverAlt || post.title} />
              {post.coverAlt ? <figcaption>{post.coverAlt}</figcaption> : null}
            </figure>
          ) : null}

          <div className="blog-article-body">
            <BlogMarkdown markdown={post.body} />
          </div>
        </article>

        {post.faqs?.length ? (
          <section className="sub-section">
            <div className="section-intro center">
              <p className="eyebrow">COMMON QUESTIONS</p>
              <h2>Quick answers</h2>
            </div>
            <div className="faq-list faq-list-page">
              {post.faqs.map(({ q, a }) => (
                <details className="faq-item" key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}

        {related.length > 0 ? (
          <section className="sub-section sub-section-soft">
            <div className="section-intro center">
              <p className="eyebrow">KEEP READING</p>
              <h2>Related articles</h2>
            </div>
            <div className="blog-card-grid">
              {related.map((item) => (
                <PostCard post={item} key={item.slug} />
              ))}
            </div>
          </section>
        ) : null}

        <BlogCTA />
      </div>
    </>
  );
}

function BlogNotFoundPage() {
  return (
    <>
      <Seo
        title="Article not found"
        description="This blog article is not available. Browse the KeepMyAsset blog for guides on nominees and family assets."
        path={BLOG_PATH}
        noindex
      />
      <div className="public-site public-page blog-page">
        <BlogBackLink />
        <section className="sub-hero sub-hero-default">
          <div className="sub-hero-copy">
            <p className="eyebrow">BLOG</p>
            <h1>We could not find that article</h1>
            <p className="lead">It may have been moved. Browse the latest guides on nominees and family assets.</p>
            <div className="public-actions">
              <Link to={BLOG_PATH} className="button button-accent">
                View all articles
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export function resolveBlogPage(path) {
  if (path === BLOG_PATH || path === `${BLOG_PATH}/`) {
    return <BlogIndexPage />;
  }

  if (!path.startsWith(`${BLOG_PATH}/`)) return null;

  const slug = path.slice(BLOG_PATH.length + 1).replace(/\/$/, '');
  if (!slug || slug.includes('/')) return <BlogNotFoundPage />;

  const post = getPostBySlug(slug);
  if (!post) return <BlogNotFoundPage />;
  return <BlogArticlePage post={post} />;
}

export { BLOG_PATH };
